import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import TextAreaField from '../ui/TextAreaField';
import { api } from '../../api/client';
import { parseFormDraftResponse } from './aiFormDraftParser';
import AiDraftAssistPanel from './AiDraftAssistPanel';

export default function LocationForm({
  addItem,
  submitRef,
  showSubmit = true,
}: {
  addItem: AddItem;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
}) {
  const [location, setLocation] = useState({ name: '', description: '', connections: '', notes: '' });
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const [isDraftingWithAi, setIsDraftingWithAi] = useState(false);

  const draftWithAi = useCallback(async () => {
    if (isDraftingWithAi) return;

    const prompt = aiPrompt.trim();
    const hasCurrentDraft = [location.name, location.description, location.connections, location.notes]
      .some((value) => value.trim().length > 0);
    if (!prompt && !hasCurrentDraft) {
      setAiError('Add a prompt or some draft text before generating.');
      return;
    }

    const systemPrompt = `You draft location records for tabletop campaigns.
Return ONLY JSON with keys: name, description, connections, notes.
Rules:
- Keep details concise and practical for live play.
- connections should be plain text (comma-separated names if multiple).
- Do not include extra keys or markdown fences.`;
    const userPrompt = `Create or refine this location draft.
Current draft:
- name: ${location.name || '(empty)'}
- description: ${location.description || '(empty)'}
- connections: ${location.connections || '(empty)'}
- notes: ${location.notes || '(empty)'}
GM prompt: ${prompt || '(none)'}`;

    setIsDraftingWithAi(true);
    setAiError('');
    try {
      const result = await api.sendAssistantMessage(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        0.6
      );
      const parsed = parseFormDraftResponse(result.content, ['name', 'description', 'connections', 'notes']);
      if (!parsed) {
        throw new Error('AI returned an unexpected format. Try a more specific prompt.');
      }

      setLocation((previous) => ({
        ...previous,
        name: parsed.name ?? previous.name,
        description: parsed.description ?? previous.description,
        connections: parsed.connections ?? previous.connections,
        notes: parsed.notes ?? previous.notes,
      }));
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Failed to generate location draft with AI.');
    } finally {
      setIsDraftingWithAi(false);
    }
  }, [aiPrompt, isDraftingWithAi, location.connections, location.description, location.name, location.notes]);

  const handleSubmit = useCallback(() => {
    if (!location.name.trim()) {
      setErrors({ name: 'Location name is required.' });
      return;
    }
    addItem('locations', location);
    setLocation({ name: '', description: '', connections: '', notes: '' });
    setAiPrompt('');
    setAiError('');
    setErrors({});
  }, [addItem, location]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="field-block">
        <label className="field-label">Location Name</label>
        <input
          type="text"
          placeholder="Location Name"
          value={location.name}
          onChange={(e) => {
            setLocation({ ...location, name: e.target.value });
            if (errors.name) setErrors({});
          }}
          className={`input-field ${errors.name ? 'input-error' : ''}`}
        />
        <div className="field-meta">
          <span className={errors.name ? 'field-error' : 'field-hint'}>
            {errors.name || 'Required.'}
          </span>
        </div>
      </div>
      <TextAreaField
        label="Description"
        placeholder="Description"
        value={location.description}
        onChange={(value) => setLocation({ ...location, description: value })}
        hint="Mood, details, and points of interest."
        showCount
        minRows={4}
      />
      <div className="field-block">
        <label className="field-label">Connections</label>
        <input
          type="text"
          placeholder="Connections (other locations)"
          value={location.connections}
          onChange={(e) => setLocation({ ...location, connections: e.target.value })}
          className="input-field"
        />
        <div className="field-meta">
          <span className="field-hint">Comma-separated location names.</span>
        </div>
      </div>
      <AiDraftAssistPanel
        value={aiPrompt}
        onChange={(value) => {
          setAiPrompt(value);
          if (aiError) setAiError('');
        }}
        onGenerate={() => void draftWithAi()}
        isGenerating={isDraftingWithAi}
        error={aiError}
        hint="Ask for atmosphere, landmarks, travel links, and actionable hooks."
      />
      <TextAreaField
        label="Notes"
        placeholder="Notes"
        value={location.notes}
        onChange={(value) => setLocation({ ...location, notes: value })}
        hint="Secrets, hooks, and NPC ties."
        showCount
        minRows={4}
      />
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add Location
        </button>
      )}
    </div>
  );
}
